import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ApiFilters,
  Pagination,
  SortDirection,
  StatePageSettings,
} from '@core/core.types';
import { Util } from '@core/utils/core.util';
import { TranslateService } from '@ngx-translate/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { isEqual } from 'lodash';
import {
  catchError,
  EMPTY,
  forkJoin,
  interval,
  of,
  switchMap,
  takeWhile,
  tap,
} from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { ResultsImportUtilsService } from '../services/results-import-utils.service';
import { ResultsImportService } from '../services/results-import.service';
import { Report } from '../types/report.types';
import { Result, ResultImport, ResultImportSaga } from '../types/result.types';
import { ReportsState } from './reports.state';
import {
  CheckResultImportSagaState,
  ClearErrorsAction,
  GetImportAction,
  GetResultImportListAction,
  ImportResultsAction,
} from './result-import.actions';
import { ResetResultsPagesAction } from './results.actions';
import { ResultsState } from './results.state';

const RESULT_IMPORT_STATE_NAME = 'ResultImport';

export interface ResultImportStateModel {
  imports: { [id: string]: ResultImport };
  pages?: {
    [key: string]: {
      items: string[];
      continuationToken: string;
    };
  };
  currentPageSettings: StatePageSettings<ResultImportFilter>;
  totalCount?: number;
  errors?: string[];
  currentImportId?: string;
  activeSagas: { [key: string]: ResultImportSaga };
}

@State<ResultImportStateModel>({
  name: RESULT_IMPORT_STATE_NAME,
  defaults: {
    activeSagas: {},
    imports: {},
    pages: {},
    errors: [],
    currentImportId: null,
    currentPageSettings: {
      pageIndex: 0,
      sortSettings: {
        orderBy: 'Generated',
        sortDirection: SortDirection.desc,
        pageSize: 30,
      },
    },
  },
})
@Injectable()
export class ResultImportState {
  private resultsImportService = inject(ResultsImportService);
  private resultsImportUtilsService = inject(ResultsImportUtilsService);
  private translateService = inject(TranslateService);

  @Selector([
    ResultImportState,
    ReportsState.getReport,
    ResultsState.getResultsByIds,
  ])
  static getResults(
    state: ResultImportStateModel,
    getReport: (reportId: string) => Report,
    getResultsByIds: (resultIds: string[]) => Result[]
  ) {
    return (rawDataIds: string[]) => {
      return getResultsByIds(rawDataIds).map((result) => ({
        ...result,
        report: getReport(result.reportIds[0]),
      }));
    };
  }

  @Selector()
  static getImport(state: ResultImportStateModel) {
    return (importId: string) => state.imports[importId];
  }

  @Selector()
  static getImportsByIds(state: ResultImportStateModel) {
    return (reportIds: string[]) =>
      reportIds
        ?.map((reportId) => state.imports[reportId])
        .filter((report) => !!report) ?? [];
  }

  @Selector()
  static getCurrentPageIds(state: ResultImportStateModel) {
    const {
      currentPageSettings: { pageIndex },
      pages,
    } = state;

    if (
      Object.keys(pages).length === 0 ||
      pageIndex > Object.keys(pages).length ||
      pageIndex < 0
    ) {
      return [];
    }
    return state.pages[pageIndex].items.slice();
  }

  @Selector([
    ResultImportState.getCurrentPageIds,
    ResultImportState.getImportsByIds,
  ])
  static getCurrentPageImports(
    currentPageIds: string[],
    importsByIds: (importIds: string[]) => ResultImport[]
  ) {
    return importsByIds(currentPageIds);
  }

  @Selector()
  static getPagination({
    currentPageSettings,
    totalCount,
    pages,
  }: ResultImportStateModel): Pagination<ResultImportFilter> {
    return {
      currentPageSettings: {
        ...currentPageSettings,
      },
      totalCount,
      pagesSize: Object.keys(pages).length,
      collectionSize: currentPageSettings.sortSettings.pageSize,
    };
  }

  @Selector()
  static getErrors(state: ResultImportStateModel) {
    return state.errors.map((e) => ({ error: e }));
  }

  @Selector()
  static getCurrentImportId(state: ResultImportStateModel) {
    return state.currentImportId;
  }

  @Action(ImportResultsAction)
  importResultsAction(
    { dispatch, patchState, getState }: StateContext<ResultImportStateModel>,
    { importerVersion, files, signeeId }: ImportResultsAction
  ) {
    return forkJoin(
      Array.from(files).map((file) => {
        return fromPromise(
          new Promise<{ fileName: string; data: string }>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const base64String = (reader.result as string).split(',')[1];
              resolve({ fileName: file.name, data: base64String });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          })
        );
      })
    ).pipe(
      switchMap((files) =>
        this.resultsImportService.importResults({
          importerVersion,
          signeeId,
          files,
        })
      ),
      catchError((err: unknown) => {
        patchState({
          errors:
            err instanceof HttpErrorResponse
              ? ((err.error?.errors as string[]) ?? [])
              : [this.translateService.instant('results.import.fileReadError')],
        });

        return of(null);
      }),
      tap((resp: { id: string }) => {
        if (resp?.id) {
          patchState({
            activeSagas: {
              ...getState().activeSagas,
              [resp.id]: { completed: false, id: resp.id },
            },
          });
        }
      }),
      switchMap((resp: { id: string }) =>
        resp?.id
          ? interval(500).pipe(
              switchMap(() =>
                dispatch(new CheckResultImportSagaState(resp.id))
              ),
              switchMap(() => {
                const saga = getState().activeSagas[resp.id];

                if (saga.completed) {
                  if (resp?.id) {
                    patchState({ errors: [], currentImportId: resp.id });
                  }

                  return dispatch(new ResetResultsPagesAction());
                }

                return of(null);
              }),
              takeWhile(() => !getState().activeSagas[resp.id]?.completed)
            )
          : of(null)
      )
    );
  }

  @Action(CheckResultImportSagaState)
  checkResultImportSagaState(
    { patchState, getState }: StateContext<ResultImportStateModel>,
    { importId }: CheckResultImportSagaState
  ) {
    const saga = getState().activeSagas[importId];

    return this.resultsImportService.checkSagaState(importId).pipe(
      tap((resp: { completed: boolean; id: string }) => {
        if (!isEqual(resp, saga)) {
          patchState({
            activeSagas: {
              ...getState().activeSagas,
              [importId]: { completed: resp.completed, id: resp.id },
            },
          });
        }
      })
    );
  }

  @Action(GetImportAction)
  getImportAction(
    { getState, patchState }: StateContext<ResultImportStateModel>,
    { importId, prependOnPage = false }: GetImportAction
  ) {
    if (!importId) {
      return null;
    }

    return this.resultsImportService.getImport(importId).pipe(
      tap((resultImport) => {
        const state = getState();
        const updates: Partial<ResultImportStateModel> = {
          imports: {
            ...state.imports,
            [resultImport.id]: resultImport,
          },
        };

        if (prependOnPage) {
          const pages = { ...state.pages };
          if (pages[0]) {
            pages[0] = {
              ...pages[0],
              items: [
                resultImport.id,
                ...pages[0].items.filter((id) => id !== resultImport.id),
              ],
            };
            updates.pages = pages;
          }
        }

        patchState(updates);
      })
    );
  }

  @Action(GetResultImportListAction)
  getResultImportListAction(
    { getState, patchState, dispatch }: StateContext<ResultImportStateModel>,
    {
      filters,
      headers,
      pageToLoad = 1,
      reload = false,
    }: GetResultImportListAction
  ) {
    // Appends objects are added, in case of separate state @Action calls, as they would otherwise override each other!
    const importsAppends = {};
    const importsList = { ...getState().imports };

    const state = getState();
    let pages = { ...state.pages };
    const currentPageSettings = {
      ...state.currentPageSettings,
      sortSettings: { ...state.currentPageSettings.sortSettings },
      filterSettings: { ...state.currentPageSettings.filterSettings },
    };
    let totalCount = state.totalCount;

    let actualPageToLoad = pageToLoad ? pageToLoad - 1 : undefined;

    // Update pagination in case of listing pages.
    if (actualPageToLoad !== undefined) {
      currentPageSettings.pageIndex = actualPageToLoad;

      // Check that filters are same as they were for previous page.
      if (
        !isEqual(currentPageSettings.filterSettings, filters) ||
        currentPageSettings.pageSize !== headers.pagingTop ||
        currentPageSettings.sortSettings.sortDirection !==
          headers.orderDirection ||
        currentPageSettings.sortSettings.orderBy !== headers.orderBy
      ) {
        // If filters are different as they were -> update filters, reset pages object and go back to page 1.
        currentPageSettings.pageSize = headers.pagingTop;
        currentPageSettings.sortSettings.sortDirection = headers.orderDirection;
        currentPageSettings.sortSettings.orderBy = headers.orderBy;
        currentPageSettings.sortSettings.pageSize = headers.pagingTop;
        currentPageSettings.filterSettings = filters;

        actualPageToLoad = 0;
        reload = true;
        pages = {};
        currentPageSettings.pageIndex = actualPageToLoad;
      }
    }

    // If reload is not required, check if we already hold service data with selected properties.
    if (filters.ids && !reload) {
      filters.ids = filters.ids.filter((item) => !importsList[item]);

      // If we filter out all IDs, return early!
      if (!filters.ids.length) {
        return EMPTY;
      }
    }

    if (filters.ids?.length > 100) {
      return forkJoin(
        Util.chunkArray(filters.ids, 100).map((ids) =>
          dispatch(
            new GetResultImportListAction({ ids }, headers, null, reload)
          )
        )
      );
    }

    // Get selected page continuation token, if not first page
    if (actualPageToLoad > 0) {
      headers.continuationToken =
        pages[actualPageToLoad - 1]?.continuationToken;
    }

    //If not listing page, page doesnt exist or reload is demanded -> call API
    if (actualPageToLoad === undefined || !pages[actualPageToLoad] || reload) {
      return this.resultsImportService
        .listImports(
          this.resultsImportUtilsService.buildHttpParams(filters),
          headers
        )
        .pipe(
          tap((response) => {
            response.items.forEach((resultImport) => {
              importsAppends[resultImport.id] = resultImport;
            });

            // Add/update pages
            if (actualPageToLoad !== undefined) {
              totalCount = response.totalCount;
              pages[actualPageToLoad] = {
                items: response.items.map((resultImport) => resultImport.id),
                continuationToken: response.continuationToken,
              };
            }
          }),
          tap(() => {
            // get latest state again as state might be patched in some other @Action
            const oldState = getState();
            patchState({
              imports: { ...oldState.imports, ...importsAppends },
              pages,
              totalCount,
              currentPageSettings,
            });
          }),
          catchError(() => {
            patchState({});
            return of(null);
          })
        );
    } else {
      // If page exists -> update state so pagination is updated.
      patchState({ currentPageSettings });
      return EMPTY;
    }
  }

  @Action(ClearErrorsAction)
  clearErrorsAction({ patchState }: StateContext<ResultImportStateModel>) {
    patchState({ errors: [] });
  }
}

export interface ResultImportFilter extends ApiFilters {
  ids?: string[];
  laboratory?: string;
}
